import { db } from 'firebase/firebase';
import { collection, doc, getDoc, getDocs } from 'firebase/firestore';

import { FSCollections } from '@src/constants/database';
import { apiCallable } from '@src/util/cloud-functions/apiClient';
import { API_OPERATIONS } from '@src/util/cloud-functions/operations';
import safeExecute from '@src/util/safeExecute';
import { sendErrorToSentry } from '@src/util/sentry/utils';

class ExecutionRepository {
  /**
   * Get the path to the execution document
   * @param {string} organizationId - Organization ID
   * @param {string} divisionId - Division ID
   * @param {string} executionId - Execution ID
   * @returns {string} Path to execution document
   */
  getExecutionPath(organizationId, divisionId, executionId) {
    return `${FSCollections.ORGANIZATIONS}/${organizationId}/${FSCollections.DIVISIONS}/${divisionId}/${FSCollections.EXECUTIONS}/${executionId}`;
  }

  /**
   * Get the path to the triggers subcollection
   * @param {string} organizationId - Organization ID
   * @param {string} divisionId - Division ID
   * @param {string} executionId - Execution ID
   * @returns {string} Path to triggers subcollection
   */
  getTriggersPath(organizationId, divisionId, executionId) {
    return `${this.getExecutionPath(organizationId, divisionId, executionId)}/${FSCollections.TRIGGERS}`;
  }

  /**
   * Get execution by ID from Firestore
   * @param {string} organizationId - Organization ID
   * @param {string} divisionId - Division ID
   * @param {string} executionId - Execution ID
   * @returns {Promise<Object|null>} Execution data or null if not found
   */
  async get(organizationId, divisionId, executionId) {
    return safeExecute(async () => {
      if (!organizationId || !divisionId || !executionId) {
        return null;
      }

      const executionRef = doc(db, this.getExecutionPath(organizationId, divisionId, executionId));

      const executionDoc = await getDoc(executionRef);

      if (executionDoc.exists()) {
        return {
          id: executionDoc.id,
          ...executionDoc.data(),
        };
      }

      return null;
    });
  }

  /**
   * Get execution triggers from Firestore
   * @param {string} organizationId - Organization ID
   * @param {string} divisionId - Division ID
   * @param {string} executionId - Execution ID
   * @returns {Promise<Array<Object>|null>} Array of triggers or null if not found
   */
  async getTriggers(organizationId, divisionId, executionId) {
    return safeExecute(async () => {
      if (!organizationId || !divisionId || !executionId) {
        return null;
      }

      const errorContext = {
        functionName: 'ExecutionRepository.getTriggers',
        organizationId,
        divisionId,
        executionId,
      };

      try {
        const triggersRef = collection(
          db,
          this.getTriggersPath(organizationId, divisionId, executionId),
        );
        const triggersSnapshot = await getDocs(triggersRef);

        if (!triggersSnapshot.empty) {
          const triggersData = [];
          triggersSnapshot.docs.forEach(triggerDoc => {
            triggersData.push({
              id: triggerDoc.id,
              ...triggerDoc.data(),
            });
          });
          return triggersData;
        }

        return null;
      } catch (error) {
        sendErrorToSentry(error, {
          ...errorContext,
          message: 'Error fetching triggers subcollection',
        });
        return null;
      }
    });
  }

  async listElastic(params) {
    return safeExecute(async () => {
      const genericFindFn = apiCallable(API_OPERATIONS.QUERY);
      const response = await genericFindFn(params);
      const responseData = response.data;
      if (!responseData?.hits) return { data: [], total: 0 };

      return responseData;
    });
  }
}

export default ExecutionRepository;
